import { useIM, ConferenceType } from '@wui/im'
import { defineStore } from 'pinia'
import { v4 as uuid } from 'uuid'
import { ref } from 'vue'

import { useStoreRooms } from '@/app/store/pinia/rooms/rooms'

import { useIntercomRoomParticipantsStatusesStore } from '@/entities/room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useAutoAnswer } from '@/shared/composables/use-auto-answer'
import { emitter } from '@/shared/services/emitter'
import { eventBus, useWebRTC } from '@/shared/services/jssip'
import { EmitterEvents, ChangeActiveParticipantsCountInActiveRoomEvent } from '@/shared/types/emitter-events'

const KEY_SEPARATOR = '_'
const MEDIA_REQUEST_ADD_TYPE = 'media-request-add'
const MEDIA_REQUEST_REMOVE_TYPE = 'media-request-remove'
const VIDEO_ENABLING_ALLOWED = 'video-enabling-allowed'
const VIDEO_ENABLING_BANNED = 'video-enabling-banned'
const AUDIO_ENABLING_ALLOWED = 'audio-enabling-allowed'
const AUDIO_ENABLING_BANNED = 'audio-enabling-banned'
const ADD_AWAITING_CONTACT = 'add-awaiting-contact'
const REJECT_MEDIA_REQUEST = 'reject-media-request'
const MEDIA_ENABLING_REQUEST = 'media-enabling-request'
const PARTICIPATION_REQUEST_ADD_TYPE = 'participation-request-add'
const PARTICIPATION_REQUEST_REMOVE_TYPE = 'participation-request-remove'

const currentParticipantUniqueId = uuid()

export type MediaRequest = Partial<{
  audio: boolean
  video: boolean
}>

type StoredRequest = MediaRequest & { id: string, targetNumber: string, callId?: string }
export type StoredEnablingMediaRequest = MediaRequest & { name: string }
type StoredParticipationRequest = MediaRequest & { id: string, targetNumber: string }
export type ParticipantUid = string

const getModeratorLabel = () => {
  const { t } = useLocalization()

  const moderator = t('common.moderator')
  return `${moderator.charAt(0).toUpperCase()}${moderator.slice(1)}`
}

export const useModeratedConferenceParticipantsSignalsStore = defineStore('moderated-conference-participants-signals', () => {
  const mediaRequests = ref(new Map<string, StoredRequest>())
  const requestsIntervals = ref(new Map<string, NodeJS.Timeout>())
  const requestsIdsByKey = ref(new Map<string, string>())
  const participationRequests = ref(new Map<string, StoredParticipationRequest>())
  const participationRequestsIntervals = ref(new Map<string, NodeJS.Timeout>())
  const participationRequestsIdsByKey = ref(new Map<string, string>())
  const participantsWithAudioEnabled = ref(new Set<string>())
  const participantsWithVideoEnabled = ref(new Set<string>())
  const rejectedRequestsExecutorsNamesByRoomPServed = ref(new Map<string, string>())
  const enablingMediaRequestFromModerators = ref(new Map<string, StoredEnablingMediaRequest>())

  const getMediaRequest = (roomPServed: string, callId: string): StoredRequest | undefined => mediaRequests.value.get(`${roomPServed}${KEY_SEPARATOR}${callId}`)

  const getParticipantsMediaRequestsByRoomPServed = (roomPServed: string): Map<string, StoredRequest | undefined> => {
    const result = new Map()
    Array.from(mediaRequests.value.keys())
      .filter(k => k.startsWith(roomPServed))
      .forEach(k => {
        const callId = k.split(KEY_SEPARATOR).at(1) as string
        result.set(callId, mediaRequests.value.get(k))
      })

    return result
  }

  const getParticipationRequest = (roomPServed: string, uid?: string): StoredParticipationRequest | undefined => participationRequests.value.get(`${roomPServed}${KEY_SEPARATOR}${uid ?? currentParticipantUniqueId}`)

  const getParticipationRequestsByRoomPServed = (roomPServed: string): Map<ParticipantUid, StoredParticipationRequest | undefined> => {
    const result = new Map<ParticipantUid, StoredParticipationRequest | undefined>()
    Array.from(participationRequests.value.keys())
      .filter(k => k.startsWith(roomPServed))
      .forEach(k => {
        const participantUid = k.split(KEY_SEPARATOR).at(1) as string
        result.set(participantUid, participationRequests.value.get(k))
      })

    return result
  }

  const getIsAllowedVideoEnabling = (roomPServed: string, callId: string) => participantsWithVideoEnabled.value.has(`${roomPServed}${KEY_SEPARATOR}${callId}`)

  const removeAllowedVideoEnabling = (roomPServed: string, callId: string) => participantsWithVideoEnabled.value.delete(`${roomPServed}${KEY_SEPARATOR}${callId}`)

  const getIsAllowedAudioEnabling = (roomPServed: string, callId: string) => participantsWithAudioEnabled.value.has(`${roomPServed}${KEY_SEPARATOR}${callId}`)

  const removeAllowedAudioEnabling = (roomPServed: string, callId: string) => participantsWithAudioEnabled.value.delete(`${roomPServed}${KEY_SEPARATOR}${callId}`)

  const getExecutorNameOfRejectedRequest = (roomPServed: string, callId: string) => rejectedRequestsExecutorsNamesByRoomPServed.value.get(`${roomPServed}${KEY_SEPARATOR}${callId}`)

  const getMediaEnablingRequest = (roomPServed: string, callId: string) => enablingMediaRequestFromModerators.value.get(`${roomPServed}${KEY_SEPARATOR}${callId}`)

  const removeEnablingMediaRequest = (roomPServed: string, callId: string) => enablingMediaRequestFromModerators.value.delete(`${roomPServed}${KEY_SEPARATOR}${callId}`)

  const { sendExtendedInfo, onInfoExtendedReceived } = useIM()

  const sendMediaRequest = async (roomPServed: string, request: MediaRequest, callId?: string): Promise<boolean> => {
    const { currentUser } = useCurrentUser()
    const targetNumber = currentUser.value?.internalNumber as string
    try {
      const id = uuid()
      const uid = currentParticipantUniqueId
      const result = await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: MEDIA_REQUEST_ADD_TYPE,
          id,
          uid,
          targetNumber,
          callId,
          ...request,
        },
      })

      const key = `${roomPServed}${KEY_SEPARATOR}${callId}`
      if (requestsIdsByKey.value.has(key)) {
        const id = requestsIdsByKey.value.get(key)
        clearTimeout(requestsIntervals.value.get(id as string))
        requestsIntervals.value.delete(id as string)
      }

      const intervalId = setInterval(() => {
        sendExtendedInfo({
          to: roomPServed,
          extendedParams: {
            type: MEDIA_REQUEST_ADD_TYPE,
            id,
            uid,
            targetNumber,
            callId,
            ...request,
          },
        })
      }, 60000)
      requestsIntervals.value.set(id, intervalId)
      requestsIdsByKey.value.set(key, id)

      mediaRequests.value.set(key, {
        id,
        targetNumber,
        audio: request.audio!,
        video: request.video!,
        callId: callId!,
      })

      return result
    } catch (e) {
      console.error(e)

      return false
    }
  }

  const sendParticipationRequest = async (roomPServed: string, request: MediaRequest): Promise<boolean> => {
    try {
      const { currentUser } = useCurrentUser()
      const targetNumber = currentUser.value?.internalNumber as string

      const id = uuid()
      const uid = currentParticipantUniqueId
      const result = await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: PARTICIPATION_REQUEST_ADD_TYPE,
          targetNumber,
          id,
          uid,
          ...request,
        },
      })

      const key = `${roomPServed}${KEY_SEPARATOR}${uid}`
      if (participationRequestsIdsByKey.value.has(key)) {
        const existedId = participationRequestsIdsByKey.value.get(key) as string
        clearTimeout(participationRequestsIntervals.value.get(existedId))
        participationRequestsIntervals.value.delete(existedId)
      }

      const intervalId = setInterval(() => {
        sendExtendedInfo({
          to: roomPServed,
          extendedParams: {
            type: PARTICIPATION_REQUEST_ADD_TYPE,
            targetNumber,
            id,
            uid,
            ...request,
          },
        })
      }, 60000)

      participationRequestsIntervals.value.set(id, intervalId)
      participationRequestsIdsByKey.value.set(key, id)

      participationRequests.value.set(key, { id, targetNumber, ...request })

      return result
    } catch (e) {
      console.error(e)

      return false
    }
  }

  const removeParticipationRequest = async (roomPServed: string, uniqueId?: string): Promise<void> => {
    try {
      const uid = uniqueId ?? currentParticipantUniqueId
      const key = `${roomPServed}${KEY_SEPARATOR}${uid}`
      const id = participationRequests.value.get(key)?.id
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: PARTICIPATION_REQUEST_REMOVE_TYPE,
          id,
          uid,
        },
      })

      if (uid === currentParticipantUniqueId && id) {
        clearInterval(participationRequestsIntervals.value.get(id))
        participationRequestsIntervals.value.delete(id)
      }

      participationRequests.value.delete(key)
    } catch (e) {
      console.error(e)
    }
  }

  const removeMediaRequest = async (roomPServed: string, callId?: string): Promise<void> => {
    try {
      const key = `${roomPServed}${KEY_SEPARATOR}${callId}`
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: MEDIA_REQUEST_REMOVE_TYPE,
          id: mediaRequests.value.get(key)?.id,
          callId,
        },
      })

      mediaRequests.value.delete(key)
    } catch (e) {
      console.error(e)
    }
  }

  const sendVideoEnablingAllowed = async (roomPServed: string, callId: string): Promise<void> => {
    try {
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: VIDEO_ENABLING_ALLOWED,
          callId,
        },
      })

      participantsWithVideoEnabled.value.add(`${roomPServed}${KEY_SEPARATOR}${callId}`)
    } catch (e) {
      console.error(e)
    }
  }

  const sendVideoEnablingBanned = async (roomPServed: string, callId: string): Promise<void> => {
    try {
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: VIDEO_ENABLING_BANNED,
          callId,
        },
      })

      participantsWithVideoEnabled.value.delete(`${roomPServed}${KEY_SEPARATOR}${callId}`)
    } catch (e) {
      console.error(e)
    }
  }

  const sendAudioEnablingAllowed = async (roomPServed: string, callId: string): Promise<void> => {
    try {
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: AUDIO_ENABLING_ALLOWED,
          callId,
        },
      })

      participantsWithAudioEnabled.value.add(`${roomPServed}${KEY_SEPARATOR}${callId}`)
    } catch (e) {
      console.error(e)
    }
  }

  const sendAudioEnablingBanned = async (roomPServed: string, callId: string): Promise<void> => {
    try {
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: AUDIO_ENABLING_BANNED,
          callId,
        },
      })

      participantsWithAudioEnabled.value.delete(`${roomPServed}${KEY_SEPARATOR}${callId}`)
    } catch (e) {
      console.error(e)
    }
  }

  const sendAddAwaitingContact = async (roomPServed: string, targetNumber: string, audio: boolean, video: boolean, uid: ParticipantUid): Promise<void> => {
    try {
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: ADD_AWAITING_CONTACT,
          targetNumber,
          audio,
          video,
          uid,
        },
      })
    } catch (e) {
      console.error(e)
    }
  }

  const sendRejectMediaRequest = async (roomPServed: string, callId: string) => {
    try {
      const { currentUser } = useCurrentUser()
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: REJECT_MEDIA_REQUEST,
          callId,
          name: currentUser.value?.name,
        },
      })
    } catch (e) {
      console.error(e)
    }
  }

  const sendMediaEnablingRequest = async (roomPServed: string, callId: string, audio?: boolean, video?: boolean) => {
    try {
      const { currentUser } = useCurrentUser()
      await sendExtendedInfo({
        to: roomPServed,
        extendedParams: {
          type: MEDIA_ENABLING_REQUEST,
          name: currentUser.value?.name,
          callId,
          audio,
          video,
        },
      })

      if (audio && !participantsWithAudioEnabled.value.has(`${roomPServed}${KEY_SEPARATOR}${callId}`)) {
        await sendAudioEnablingAllowed(roomPServed, callId)
      }

      if (video && !participantsWithVideoEnabled.value.has(`${roomPServed}${KEY_SEPARATOR}${callId}`)) {
        await sendVideoEnablingAllowed(roomPServed, callId)
      }
    } catch (e) {
      console.error(e)
    }
  }

  onInfoExtendedReceived<{
    type: string,
    targetNumber: string,
    audio?: boolean,
    video?: boolean
    id: string,
    name?: string
    uid?: string
    callId?: string
  }>(({ executorRoom, extendedParams }) => {
    const { type, targetNumber, id, uid, callId } = extendedParams

    if (type === MEDIA_REQUEST_ADD_TYPE) {
      const key = `${executorRoom}${KEY_SEPARATOR}${callId}`
      const existedRequest = mediaRequests.value.get(key)
      const requestToAdd: StoredRequest = {
        audio: existedRequest?.audio ? true : extendedParams.audio ?? false,
        video: existedRequest?.video ? true : extendedParams.video ?? false,
        id: extendedParams.id,
        targetNumber,
        callId: callId!,
      }

      mediaRequests.value.set(key, requestToAdd)
    }

    if (type === MEDIA_REQUEST_REMOVE_TYPE) {
      const key = `${executorRoom}${KEY_SEPARATOR}${callId}`
      mediaRequests.value.delete(key)
      requestsIdsByKey.value.delete(key)

      if (requestsIntervals.value.has(id)) {
        clearInterval(requestsIntervals.value.get(id))

        requestsIntervals.value.delete(id)
      }

      return
    }

    if (type === PARTICIPATION_REQUEST_ADD_TYPE) {
      const key = `${executorRoom}${KEY_SEPARATOR}${uid}`
      const existedRequest = participationRequests.value.get(key)
      const requestToAdd: StoredParticipationRequest = {
        audio: existedRequest?.audio ? true : extendedParams.audio ?? false,
        video: existedRequest?.video ? true : extendedParams.video ?? false,
        id: extendedParams.id,
        targetNumber,
      }

      participationRequests.value.set(key, requestToAdd)

      return
    }

    if (type === PARTICIPATION_REQUEST_REMOVE_TYPE) {
      const key = `${executorRoom}${KEY_SEPARATOR}${uid}`
      participationRequests.value.delete(key)
      participationRequestsIdsByKey.value.delete(key)

      if (participationRequestsIntervals.value.has(id)) {
        clearInterval(participationRequestsIntervals.value.get(id))
        participationRequestsIntervals.value.delete(id)
      }

      return
    }

    if (type === VIDEO_ENABLING_ALLOWED) {
      participantsWithVideoEnabled.value.add(`${executorRoom}${KEY_SEPARATOR}${callId}`)
      return
    }

    if (type === VIDEO_ENABLING_BANNED) {
      participantsWithVideoEnabled.value.delete(`${executorRoom}${KEY_SEPARATOR}${callId}`)
      return
    }

    if (type === AUDIO_ENABLING_ALLOWED) {
      participantsWithAudioEnabled.value.add(`${executorRoom}${KEY_SEPARATOR}${callId}`)
      return
    }

    if (type === AUDIO_ENABLING_BANNED) {
      participantsWithAudioEnabled.value.delete(`${executorRoom}${KEY_SEPARATOR}${callId}`)
      return
    }

    if (type === ADD_AWAITING_CONTACT) {
      if (currentParticipantUniqueId === uid) {
        const { changeMediaConstraints } = useAutoAnswer()
        changeMediaConstraints(executorRoom, extendedParams.audio as boolean, extendedParams.video as boolean)

        useIM().joinConference({
          pServed: executorRoom,
          video: extendedParams.video!,
          audio: extendedParams.audio!,
          registrationId: useWebRTC().registrationId.value!,
        })
      }

      return
    }

    if (type === REJECT_MEDIA_REQUEST) {
      const key = `${executorRoom}${KEY_SEPARATOR}${callId}`
      rejectedRequestsExecutorsNamesByRoomPServed.value.set(key, extendedParams.name ?? getModeratorLabel())
      setTimeout(() => rejectedRequestsExecutorsNamesByRoomPServed.value.delete(key), 60000)

      return
    }

    if (type === MEDIA_ENABLING_REQUEST) {
      const key = `${executorRoom}${KEY_SEPARATOR}${callId}`

      if (enablingMediaRequestFromModerators.value.has(key)) {
        enablingMediaRequestFromModerators.value.delete(key)
      }

      setTimeout(() => {
        enablingMediaRequestFromModerators.value.set(key, {
          audio: extendedParams.audio ?? false,
          video: extendedParams.video ?? false,
          name: extendedParams.name ?? getModeratorLabel(),
        })
      }, 0)

      setTimeout(() => enablingMediaRequestFromModerators.value.delete(key), 60000)

      return
    }
  })

  eventBus.on('callEndedInRoom', (roomPServed: string) => {
    Array.from(mediaRequests.value.keys()).forEach((key: string) => {
      if (key.startsWith(roomPServed)) {
        const request = mediaRequests.value.get(key)
        if (request && requestsIntervals.value.has(request?.id)) {
          clearInterval(requestsIntervals.value.get(request?.id))

          requestsIntervals.value.delete(request?.id)
        }

        mediaRequests.value.delete(key)
        requestsIdsByKey.value.delete(key)
        enablingMediaRequestFromModerators.value.delete(key)
        participantsWithVideoEnabled.value.clear()
        participantsWithAudioEnabled.value.clear()
      }
    })

    Array.from(participationRequests.value.keys()).forEach((key: string) => {
      if (key.startsWith(roomPServed)) {
        const request = participationRequests.value.get(key)
        if (request && participationRequestsIntervals.value.has(request.id)) {
          clearInterval(participationRequestsIntervals.value.get(request.id))

          participationRequestsIntervals.value.delete(request.id)
        }

        participationRequests.value.delete(key)
        participationRequestsIdsByKey.value.delete(key)
      }
    })
  })

  emitter.on(EmitterEvents.CHANGE_ACTIVE_PARTICIPANTS_COUNT_IN_ACTIVE_ROOM, (event) => {
    const { roomPServed, count } = event as ChangeActiveParticipantsCountInActiveRoomEvent

    const { currentUser } = useCurrentUser()
    const { getIsParticipantIsActive } = useIntercomRoomParticipantsStatusesStore()
    const { findByPServed } = useStoreRooms()

    const room = findByPServed(roomPServed)

    room?.subscribers.forEach(contact => {
      if (!getIsParticipantIsActive(roomPServed, contact.internalNumber)) {
        const key = `${roomPServed}${KEY_SEPARATOR}${contact.internalNumber}`
        participantsWithVideoEnabled.value.delete(key)
        participantsWithAudioEnabled.value.delete(key)
      }
    })

    if (
      !currentUser.value ||
      count === 1 ||
      room?.mode !== ConferenceType.MODERATED ||
      !getIsParticipantIsActive(roomPServed, currentUser.value.internalNumber)
    ) {
      return
    }
  })

  return {
    getMediaRequest,
    sendMediaRequest,
    getParticipantsMediaRequestsByRoomPServed,
    removeMediaRequest,
    getParticipationRequest,
    sendParticipationRequest,
    getParticipationRequestsByRoomPServed,
    removeParticipationRequest,
    sendVideoEnablingAllowed,
    sendVideoEnablingBanned,
    sendAddAwaitingContact,
    sendAudioEnablingAllowed,
    sendAudioEnablingBanned,
    getIsAllowedVideoEnabling,
    getIsAllowedAudioEnabling,
    sendRejectMediaRequest,
    getExecutorNameOfRejectedRequest,
    sendMediaEnablingRequest,
    getMediaEnablingRequest,
    removeEnablingMediaRequest,
    removeAllowedVideoEnabling,
    removeAllowedAudioEnabling,
  }
})
